import { useState, useEffect, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { 
  Users, 
  FileText, 
  AlertTriangle, 
  Clock, 
  Eye, 
  Activity,
  ChevronRight,
  PlayCircle,
  Search,
  X,
  Download,
  CheckSquare,
  StickyNote,
  ToggleLeft,
  ToggleRight
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { format, formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';
import SupervisorNotificationBell from './SupervisorNotificationBell';
import QuickStatsWidget, { StatItem } from '@/components/dashboard/QuickStatsWidget';
import NotificationCenter from '@/components/dashboard/NotificationCenter';
import GoalStatusWidget from '@/components/dashboard/GoalStatusWidget';
import { useDashboardTrends } from '@/hooks/useTrendCalculator';
import { usePersistedTrendPeriod } from '@/hooks/usePersistedTrendPeriod';
import { useGoalThresholds } from '@/hooks/useGoalThresholds';

interface AssignedPatient {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
  dob: string | null;
  is_active: boolean;
  created_at: string;
  assigned_at: string;
  totalSessions: number;
  completedSessions: number;
  lastSession: string | null;
  hasRedFlags: boolean;
  latestRecommendation: string | null;
}

export default function SupervisorDashboard() {
  const { user } = useAdminAuth();
  const navigate = useNavigate();
  const [patients, setPatients] = useState<AssignedPatient[]>([]);
  const [loading, setLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive' | 'red_flags'>('all');
  const [selectedPatients, setSelectedPatients] = useState<Set<string>>(new Set());
  const [bulkNoteDialogOpen, setBulkNoteDialogOpen] = useState(false);
  const [bulkNoteContent, setBulkNoteContent] = useState('');
  const [bulkNoteType, setBulkNoteType] = useState<string>('general');
  const [isSavingNotes, setIsSavingNotes] = useState(false);
  const [trendPeriod, setTrendPeriod] = usePersistedTrendPeriod('week');
  const [stats, setStats] = useState({
    totalAssigned: 0,
    activePatients: 0,
    totalSessions: 0,
    redFlagCases: 0
  });
  
  // Get trends for supervisor's agency
  const { trends } = useDashboardTrends({ 
    agencyId: user?.agency_id || undefined, 
    period: trendPeriod 
  });
  const { thresholds } = useGoalThresholds(user?.agency_id || undefined, trendPeriod);

  // Filter patients based on search query and status filter
  const filteredPatients = useMemo(() => {
    let result = patients;
    
    // Apply status filter
    switch (statusFilter) {
      case 'active':
        result = result.filter(p => p.is_active);
        break;
      case 'inactive':
        result = result.filter(p => !p.is_active);
        break;
      case 'red_flags':
        result = result.filter(p => p.hasRedFlags);
        break;
    }
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(p => 
        p.first_name.toLowerCase().includes(query) ||
        p.last_name.toLowerCase().includes(query) ||
        p.email.toLowerCase().includes(query) ||
        `${p.first_name} ${p.last_name}`.toLowerCase().includes(query)
      );
    }
    
    return result;
  }, [patients, searchQuery, statusFilter]);

  // Trigger pulse animation on realtime update
  const triggerUpdateAnimation = () => {
    setIsUpdating(true);
    setTimeout(() => setIsUpdating(false), 1500);
  };

  useEffect(() => {
    if (user?.id) {
      loadAssignedPatients();

      // Set up real-time subscriptions
      const channel = supabase
        .channel('supervisor-dashboard-realtime')
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'patient_assignments' },
          () => {
            console.log('Patient assignments changed, refreshing supervisor dashboard...');
            triggerUpdateAnimation();
            loadAssignedPatients();
          }
        )
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'patients' },
          () => {
            console.log('Patients changed, refreshing supervisor dashboard...');
            triggerUpdateAnimation();
            loadAssignedPatients();
          }
        )
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'triage_sessions' },
          () => {
            console.log('Triage sessions changed, refreshing supervisor dashboard...');
            triggerUpdateAnimation();
            loadAssignedPatients();
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [user?.id]);

  const loadAssignedPatients = async () => {
    if (!user?.id) return;
    
    try {
      // Get assigned patient IDs
      const { data: assignments, error: assignError } = await supabase
        .from('patient_assignments')
        .select('patient_id, assigned_at')
        .eq('staff_user_id', user.id)
        .is('revoked_at', null);

      if (assignError) throw assignError;

      if (!assignments || assignments.length === 0) {
        setPatients([]);
        setStats({ totalAssigned: 0, activePatients: 0, totalSessions: 0, redFlagCases: 0 });
        setLoading(false);
        return;
      }

      const patientIds = assignments.map(a => a.patient_id);
      const assignmentMap = new Map(assignments.map(a => [a.patient_id, a.assigned_at]));

      // Get patient details
      const { data: patientsData, error: patientsError } = await supabase
        .from('patients')
        .select('id, first_name, last_name, email, phone, dob, is_active, created_at')
        .in('id', patientIds);

      if (patientsError) throw patientsError;

      // Get triage sessions for these patients
      const { data: sessions, error: sessionsError } = await supabase
        .from('triage_sessions')
        .select('id, patient_id, completed_at, red_flags, recommendation, created_at')
        .in('patient_id', patientIds)
        .order('created_at', { ascending: false });

      if (sessionsError) throw sessionsError;

      // Aggregate data per patient
      const patientSessionMap = new Map<string, {
        total: number;
        completed: number;
        lastSession: string | null;
        hasRedFlags: boolean;
        latestRecommendation: string | null;
      }>();

      patientIds.forEach(id => {
        patientSessionMap.set(id, {
          total: 0,
          completed: 0,
          lastSession: null,
          hasRedFlags: false,
          latestRecommendation: null
        });
      });

      sessions?.forEach(session => {
        const existing = patientSessionMap.get(session.patient_id);
        if (existing) {
          existing.total++;
          if (session.completed_at) existing.completed++;
          if (session.red_flags) existing.hasRedFlags = true;
          if (!existing.lastSession) {
            existing.lastSession = session.created_at;
            existing.latestRecommendation = session.recommendation;
          }
        }
      });

      // Build final patient list
      const enrichedPatients: AssignedPatient[] = (patientsData || []).map(p => {
        const sessionData = patientSessionMap.get(p.id)!;
        return {
          ...p,
          assigned_at: assignmentMap.get(p.id) || '',
          totalSessions: sessionData.total,
          completedSessions: sessionData.completed,
          lastSession: sessionData.lastSession,
          hasRedFlags: sessionData.hasRedFlags,
          latestRecommendation: sessionData.latestRecommendation
        };
      });

      // Sort by last activity (most recent first)
      enrichedPatients.sort((a, b) => {
        if (a.lastSession && b.lastSession) {
          return new Date(b.lastSession).getTime() - new Date(a.lastSession).getTime();
        }
        if (a.lastSession) return -1;
        if (b.lastSession) return 1;
        return new Date(b.assigned_at).getTime() - new Date(a.assigned_at).getTime();
      });

      setPatients(enrichedPatients);
      setStats({
        totalAssigned: enrichedPatients.length,
        activePatients: enrichedPatients.filter(p => p.is_active).length,
        totalSessions: enrichedPatients.reduce((sum, p) => sum + p.totalSessions, 0),
        redFlagCases: enrichedPatients.filter(p => p.hasRedFlags).length
      });
    } catch (error) {
      console.error('Error loading assigned patients:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRecommendationBadge = (recommendation: string | null) => {
    if (!recommendation) return null;
    const variants: Record<string, { variant: 'default' | 'secondary' | 'destructive' | 'outline'; label: string }> = {
      'HOME': { variant: 'secondary', label: 'Home Care' },
      'PCP': { variant: 'outline', label: 'See Doctor' },
      'URGENT_CARE': { variant: 'default', label: 'Urgent Care' },
      'ER_NOW': { variant: 'destructive', label: 'ER Now' }
    };
    const config = variants[recommendation] || { variant: 'outline', label: recommendation };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const handleStartTriage = async (patient: AssignedPatient) => {
    if (!user?.id) return;
    
    if (!patient.is_active) {
      toast.error('Cannot start triage for inactive patient');
      return;
    }

    try {
      // Create impersonation session
      const { data: session, error } = await supabase
        .from('impersonation_sessions')
        .insert({
          impersonator_user_id: user.id,
          impersonated_patient_id: patient.id,
          impersonation_type: 'patient',
          ip_address: '0.0.0.0'
        })
        .select('id')
        .single();

      if (error) throw error;

      // Navigate to triage with impersonation params
      navigate(`/triage?impersonate=patient&pid=${patient.id}&sid=${session.id}`);
      toast.success(`Starting triage for ${patient.first_name} ${patient.last_name}`);
    } catch (error) {
      console.error('Error starting impersonation:', error);
      toast.error('Failed to start triage session');
    }
  };

  // Bulk selection handlers
  const toggleSelectPatient = (patientId: string) => {
    setSelectedPatients(prev => {
      const next = new Set(prev);
      if (next.has(patientId)) {
        next.delete(patientId);
      } else {
        next.add(patientId);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedPatients.size === filteredPatients.length) {
      setSelectedPatients(new Set());
    } else {
      setSelectedPatients(new Set(filteredPatients.map(p => p.id)));
    }
  };

  const clearSelection = () => {
    setSelectedPatients(new Set());
  };

  const isAllSelected = filteredPatients.length > 0 && selectedPatients.size === filteredPatients.length;
  const isSomeSelected = selectedPatients.size > 0 && selectedPatients.size < filteredPatients.length;

  // Export selected patients to CSV
  const exportSelectedPatients = () => {
    const selectedData = patients.filter(p => selectedPatients.has(p.id));
    
    if (selectedData.length === 0) {
      toast.error('No patients selected');
      return;
    }

    const headers = [
      'First Name',
      'Last Name',
      'Email',
      'Phone',
      'DOB',
      'Status',
      'Total Sessions',
      'Completed Sessions',
      'Last Activity',
      'Latest Recommendation',
      'Red Flags',
      'Assigned Date'
    ];

    const rows = selectedData.map(p => [
      p.first_name,
      p.last_name,
      p.email,
      p.phone || '',
      p.dob || '',
      p.is_active ? 'Active' : 'Inactive',
      p.totalSessions.toString(),
      p.completedSessions.toString(),
      p.lastSession ? format(new Date(p.lastSession), 'yyyy-MM-dd HH:mm') : '',
      p.latestRecommendation || '',
      p.hasRedFlags ? 'Yes' : 'No',
      format(new Date(p.assigned_at), 'yyyy-MM-dd')
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `assigned-patients-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.success(`Exported ${selectedData.length} patient(s) to CSV`);
    clearSelection();
  };

  // Bulk start triage for first active selected patient
  const handleBulkStartTriage = async () => {
    const selectedData = patients.filter(p => selectedPatients.has(p.id) && p.is_active);
    
    if (selectedData.length === 0) {
      toast.error('No active patients selected');
      return;
    }

    // Start with the first active selected patient
    const firstPatient = selectedData[0];
    await handleStartTriage(firstPatient);
  };

  // Get count of active selected patients for bulk triage
  const activeSelectedCount = patients.filter(p => selectedPatients.has(p.id) && p.is_active).length;

  // Bulk add notes to selected patients
  const handleBulkAddNotes = async () => {
    if (!bulkNoteContent.trim()) {
      toast.error('Please enter a note');
      return;
    }

    if (!user?.id) {
      toast.error('User not authenticated');
      return;
    }

    setIsSavingNotes(true);
    const selectedIds = Array.from(selectedPatients);
    let successCount = 0;
    let errorCount = 0;

    for (const patientId of selectedIds) {
      const { error } = await supabase
        .from('patient_notes')
        .insert({
          patient_id: patientId,
          author_user_id: user.id,
          content: bulkNoteContent.trim(),
          note_type: bulkNoteType,
          is_private: false
        });

      if (error) {
        console.error('Error adding note for patient:', patientId, error);
        errorCount++;
      } else {
        successCount++;
      }
    }

    setIsSavingNotes(false);

    if (successCount > 0) {
      toast.success(`Added notes to ${successCount} patient(s)`);
    }
    if (errorCount > 0) {
      toast.error(`Failed to add notes to ${errorCount} patient(s)`);
    }

    setBulkNoteDialogOpen(false);
    setBulkNoteContent('');
    setBulkNoteType('general');
    clearSelection();
  };

  // Bulk toggle status for selected patients
  const handleBulkToggleStatus = async (newStatus: boolean) => {
    const selectedIds = Array.from(selectedPatients);
    
    if (selectedIds.length === 0) {
      toast.error('No patients selected');
      return;
    }

    let successCount = 0;
    let errorCount = 0;

    for (const patientId of selectedIds) {
      const { error } = await supabase
        .from('patients')
        .update({ is_active: newStatus })
        .eq('id', patientId);

      if (error) {
        console.error('Error updating patient status:', patientId, error);
        errorCount++;
      } else {
        successCount++;
      }
    }

    if (successCount > 0) {
      // Update local state
      setPatients(prev => 
        prev.map(p => 
          selectedPatients.has(p.id) ? { ...p, is_active: newStatus } : p
        )
      );
      toast.success(`${newStatus ? 'Activated' : 'Deactivated'} ${successCount} patient(s)`);
    }
    if (errorCount > 0) {
      toast.error(`Failed to update ${errorCount} patient(s)`);
    }

    clearSelection();
  };

  // Get counts for bulk status toggle
  const selectedActiveCount = patients.filter(p => selectedPatients.has(p.id) && p.is_active).length;
  const selectedInactiveCount = patients.filter(p => selectedPatients.has(p.id) && !p.is_active).length;

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Live Update Indicator */}
      {isUpdating && (
        <div className="flex items-center gap-2 px-3 py-1 bg-success/10 text-success rounded-full text-sm font-medium animate-fade-in w-fit">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-success opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-success"></span>
          </span>
          Live update
        </div>
      )}

      {/* Stats Cards and Notification Center */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <QuickStatsWidget 
            stats={[
              { 
                title: 'Assigned Patients', 
                value: stats.totalAssigned, 
                description: `${stats.activePatients} active`, 
                icon: Users, 
                color: 'text-blue-600',
                onClick: () => setStatusFilter('all'),
                trend: trends.patients ? { 
                  value: trends.patients.percentChange, 
                  isPositive: trends.patients.isPositive, 
                  label: trends.patients.label,
                  sparklineData: trends.patients.sparklineData,
                  threshold: thresholds.patients
                } : undefined
              },
              { 
                title: 'Total Sessions', 
                value: stats.totalSessions, 
                description: 'Across all patients', 
                icon: FileText, 
                color: 'text-green-600',
                trend: trends.sessions ? { 
                  value: trends.sessions.percentChange, 
                  isPositive: trends.sessions.isPositive, 
                  label: trends.sessions.label,
                  sparklineData: trends.sessions.sparklineData,
                  threshold: thresholds.sessions
                } : undefined
              },
              { 
                title: 'Red Flag Cases', 
                value: stats.redFlagCases, 
                description: 'Patients with alerts', 
                icon: AlertTriangle, 
                color: 'text-red-600',
                onClick: () => setStatusFilter('red_flags'),
                trend: trends.redFlags ? { 
                  value: trends.redFlags.percentChange, 
                  isPositive: trends.redFlags.isPositive, 
                  label: trends.redFlags.label,
                  sparklineData: trends.redFlags.sparklineData,
                  threshold: thresholds.redFlags
                } : undefined
              },
              { 
                title: 'Coverage', 
                value: `${stats.totalAssigned > 0 ? Math.round((stats.activePatients / stats.totalAssigned) * 100) : 0}%`, 
                description: 'Active patient rate', 
                icon: Activity, 
                color: 'text-purple-600',
                onClick: () => setStatusFilter('active')
              },
            ]}
            columns={2}
            isUpdating={isUpdating}
            trendPeriod={trendPeriod}
            onTrendPeriodChange={setTrendPeriod}
          />
        </div>
        <GoalStatusWidget agencyId={user?.agency_id} compact />
        <NotificationCenter role="supervisor" />
      </div>

      {/* Assigned Patients List */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                My Assigned Patients
              </CardTitle>
              <CardDescription>
                Quick access to your assigned patients and their triage history
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <SupervisorNotificationBell />
              {patients.length > 0 && (
                <div className="relative w-full sm:w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search patients..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 pr-9"
                  />
                  {searchQuery && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
                      onClick={() => setSearchQuery('')}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {patients.length > 0 && (
            <Tabs value={statusFilter} onValueChange={(v) => setStatusFilter(v as typeof statusFilter)}>
              <TabsList>
                <TabsTrigger value="all" className="gap-2">
                  All
                  <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                    {patients.length}
                  </Badge>
                </TabsTrigger>
                <TabsTrigger value="active" className="gap-2">
                  Active
                  <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                    {patients.filter(p => p.is_active).length}
                  </Badge>
                </TabsTrigger>
                <TabsTrigger value="inactive" className="gap-2">
                  Inactive
                  <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                    {patients.filter(p => !p.is_active).length}
                  </Badge>
                </TabsTrigger>
                <TabsTrigger value="red_flags" className="gap-2">
                  <AlertTriangle className="h-3 w-3" />
                  Red Flags
                  <Badge variant="destructive" className="h-5 px-1.5 text-xs">
                    {patients.filter(p => p.hasRedFlags).length}
                  </Badge>
                </TabsTrigger>
              </TabsList>
            </Tabs>
          )}

          {/* Bulk Action Bar */}
          {selectedPatients.size > 0 && (
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-3 bg-primary/10 rounded-lg border border-primary/20">
              <div className="flex items-center gap-3">
                <CheckSquare className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium">
                  {selectedPatients.size} patient{selectedPatients.size > 1 ? 's' : ''} selected
                </span>
                <Button variant="ghost" size="sm" onClick={clearSelection}>
                  Clear
                </Button>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button 
                  variant="default" 
                  size="sm" 
                  onClick={handleBulkStartTriage}
                  disabled={activeSelectedCount === 0}
                >
                  <PlayCircle className="h-4 w-4 mr-1" />
                  Start Triage
                  {activeSelectedCount > 0 && (
                    <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-xs bg-primary-foreground/20">
                      {activeSelectedCount}
                    </Badge>
                  )}
                </Button>
                <Button variant="outline" size="sm" onClick={() => setBulkNoteDialogOpen(true)}>
                  <StickyNote className="h-4 w-4 mr-1" />
                  Add Notes
                </Button>
                {selectedInactiveCount > 0 && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handleBulkToggleStatus(true)}
                    className="text-green-600 hover:text-green-700 hover:bg-green-50"
                  >
                    <ToggleRight className="h-4 w-4 mr-1" />
                    Activate
                    <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-xs">
                      {selectedInactiveCount}
                    </Badge>
                  </Button>
                )}
                {selectedActiveCount > 0 && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handleBulkToggleStatus(false)}
                    className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                  >
                    <ToggleLeft className="h-4 w-4 mr-1" />
                    Deactivate
                    <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-xs">
                      {selectedActiveCount}
                    </Badge>
                  </Button>
                )}
                <Button variant="outline" size="sm" onClick={exportSelectedPatients}>
                  <Download className="h-4 w-4 mr-1" />
                  Export CSV
                </Button>
              </div>
            </div>
          )}

          {patients.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium">No patients assigned</p>
              <p className="text-sm">Contact your agency admin to get patients assigned to you.</p>
            </div>
          ) : filteredPatients.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Search className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium">No matching patients</p>
              <p className="text-sm">Try adjusting your search or filter</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox
                      checked={isAllSelected}
                      onCheckedChange={toggleSelectAll}
                      aria-label="Select all patients"
                      className={isSomeSelected ? 'data-[state=checked]:bg-primary/50' : ''}
                    />
                  </TableHead>
                  <TableHead>Patient</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Sessions</TableHead>
                  <TableHead>Last Activity</TableHead>
                  <TableHead>Latest Result</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPatients.map((patient) => (
                  <TableRow key={patient.id} className="group">
                    <TableCell>
                      <Checkbox
                        checked={selectedPatients.has(patient.id)}
                        onCheckedChange={() => toggleSelectPatient(patient.id)}
                        aria-label={`Select ${patient.first_name} ${patient.last_name}`}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-medium flex items-center gap-2">
                          {patient.first_name} {patient.last_name}
                          {patient.hasRedFlags && (
                            <AlertTriangle className="h-4 w-4 text-destructive" />
                          )}
                        </span>
                        <span className="text-xs text-muted-foreground">{patient.email}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={patient.is_active ? 'default' : 'secondary'}>
                        {patient.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <FileText className="h-4 w-4 text-muted-foreground" />
                        <span>{patient.totalSessions}</span>
                        <span className="text-muted-foreground text-xs">
                          ({patient.completedSessions} completed)
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {patient.lastSession ? (
                        <div className="flex items-center gap-1 text-sm">
                          <Clock className="h-4 w-4 text-muted-foreground" />
                          {formatDistanceToNow(new Date(patient.lastSession), { addSuffix: true })}
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-sm">No sessions</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {getRecommendationBadge(patient.latestRecommendation)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => handleStartTriage(patient)}
                          disabled={!patient.is_active}
                          className="opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <PlayCircle className="h-4 w-4 mr-1" />
                          Start Triage
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/admin/patients/${patient.id}`)}
                          className="opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          View
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Bulk Add Notes Dialog */}
      <Dialog open={bulkNoteDialogOpen} onOpenChange={setBulkNoteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Note to Selected Patients</DialogTitle>
            <DialogDescription>
              This note will be added to {selectedPatients.size} selected patient{selectedPatients.size > 1 ? 's' : ''}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="note-type">Note Type</Label>
              <Select value={bulkNoteType} onValueChange={setBulkNoteType}>
                <SelectTrigger id="note-type">
                  <SelectValue placeholder="Select note type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="clinical">Clinical</SelectItem>
                  <SelectItem value="follow_up">Follow-up</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="note-content">Note Content</Label>
              <Textarea
                id="note-content"
                placeholder="Enter your note here..."
                value={bulkNoteContent}
                onChange={(e) => setBulkNoteContent(e.target.value)}
                rows={4}
                maxLength={1000}
              />
              <p className="text-xs text-muted-foreground text-right">
                {bulkNoteContent.length}/1000
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkNoteDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleBulkAddNotes} 
              disabled={isSavingNotes || !bulkNoteContent.trim()}
            >
              {isSavingNotes ? 'Adding...' : `Add to ${selectedPatients.size} Patient${selectedPatients.size > 1 ? 's' : ''}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
